import React from 'react';
import { Card, CardContent, Typography, Box, Button, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { trackEvent } from '../utils/analytics';

const WHATSAPP_NUMBER = '905325525984';

const ProductCard = ({ name, description, image, categoryLabel, icon: Icon, index }) => {
  const { t } = useTranslation();

  const requestHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `${t('products.catalog.orderMessage')} ${name}`
  )}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: (index % 4) * 0.08 }}
      viewport={{ once: true }}
      style={{ height: '100%' }}
    >
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          '&:hover .product-img': { transform: 'scale(1.07)' },
        }}
      >
        {/* Image */}
        <Box sx={{ position: 'relative', height: 200, overflow: 'hidden', bgcolor: 'rgba(16,32,50,0.04)' }}>
          <Box
            component="img"
            className="product-img"
            src={image}
            alt={name}
            loading="lazy"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              transition: 'transform 0.6s cubic-bezier(0.22,1,0.36,1)',
            }}
          />
          <Chip
            icon={Icon ? <Icon sx={{ fontSize: '16px !important', color: 'white !important' }} /> : undefined}
            label={categoryLabel}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              bgcolor: 'rgba(10,42,102,0.85)',
              color: 'white',
              fontWeight: 600,
              backdropFilter: 'blur(4px)',
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>

        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700, letterSpacing: '0.02em' }}>
              {t('products.catalog.badge')}
            </Typography>
          </Box>

          <Typography variant="h6" component="h3" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, lineHeight: 1.35 }}>
            {name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: 2.5, flexGrow: 1 }}>
            {description}
          </Typography>

          <Button
            href={requestHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('product_request_click', { product_name: name, source: 'product_card' })}
            fullWidth
            variant="contained"
            startIcon={<WhatsAppIcon />}
            sx={{
              mt: 'auto',
              background: 'linear-gradient(135deg, #25D366, #128C7E)',
              boxShadow: '0 8px 18px -8px rgba(18,140,126,0.8)',
              '&:hover': { background: 'linear-gradient(135deg, #2ee06f, #0f7a6c)' },
            }}
          >
            {t('products.catalog.request')}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ProductCard;
